import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  InputAdornment,
  MenuItem,
  TextField,
  Typography
} from '@mui/material';
import { Save as SaveIcon } from '@mui/icons-material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { de } from 'date-fns/locale';

import { MAINTENANCE_TYPES, getMaintenanceType } from '../../config/vehicleMaintenance';
import { addMonths, getVehicleDueDate } from '../../utils/vehicleDeadlines';
import { addMaintenanceRecord } from '../../redux/slices/vehiclesSlice';

const emptyForm = (type = 'tuv') => ({
  type,
  description: '',
  date: new Date(),
  nextDueDate: null,
  odometer: '',
  cost: '',
  notes: ''
});

const suggestedNextDue = (typeKey, performedOn) => {
  const type = getMaintenanceType(typeKey);
  if (!type?.tracksDue || !type.defaultIntervalMonths || !performedOn) return null;
  return addMonths(performedOn, type.defaultIntervalMonths);
};

const parseNumber = (value) => {
  if (value === '' || value === null || value === undefined) return null;
  const parsed = Number(String(value).replace(',', '.'));
  return Number.isFinite(parsed) ? parsed : null;
};

/**
 * Wartung eintragen: Art per Auswahl (TÜV, Inspektion, Eichung, Ölwechsel,
 * Sonstiges). Bei fristgebundenen Arten schlägt der Dialog die nächste
 * Fälligkeit aus dem üblichen Intervall vor, solange der Nutzer sie nicht
 * selbst gesetzt hat.
 */
const MaintenanceRecordDialog = ({ open, onClose, vehicle, initialType = 'tuv' }) => {
  const dispatch = useDispatch();
  const [form, setForm] = useState(emptyForm(initialType));
  const [dueTouched, setDueTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    const initial = emptyForm(initialType);
    initial.nextDueDate = suggestedNextDue(initialType, initial.date);
    setForm(initial);
    setDueTouched(false);
    setError('');
  }, [open, initialType]);

  const type = getMaintenanceType(form.type);
  const currentDue = type?.tracksDue ? getVehicleDueDate(vehicle, form.type) : null;

  const updateForm = (changes) => {
    setForm(prev => {
      const next = { ...prev, ...changes };
      if (!dueTouched && ('type' in changes || 'date' in changes)) {
        next.nextDueDate = suggestedNextDue(next.type, next.date);
      }
      return next;
    });
  };

  const handleTypeChange = (e) => {
    setDueTouched(false);
    setForm(prev => ({
      ...prev,
      type: e.target.value,
      nextDueDate: suggestedNextDue(e.target.value, prev.date)
    }));
  };

  const descriptionMissing = form.type === 'sonstiges' && !form.description.trim();
  const dateInvalid = !form.date || Number.isNaN(form.date.getTime());

  const handleSubmit = async () => {
    if (descriptionMissing || dateInvalid) return;
    setSaving(true);
    setError('');
    const result = await dispatch(addMaintenanceRecord({
      vehicleId: vehicle.id,
      record: {
        type: form.type,
        description: form.description,
        date: form.date.toISOString(),
        nextDueDate: type?.tracksDue && form.nextDueDate && !Number.isNaN(form.nextDueDate.getTime())
          ? form.nextDueDate.toISOString()
          : null,
        odometer: parseNumber(form.odometer),
        cost: parseNumber(form.cost),
        notes: form.notes
      }
    }));
    setSaving(false);
    if (result.error) {
      setError(result.payload || 'Die Wartung konnte nicht gespeichert werden.');
      return;
    }
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      // App.css setzt `max-width: 100% !important` auf jedes Paper und hebelt
      // damit maxWidth="sm" aus — hier gezielt wiederherstellen.
      slotProps={{ paper: { sx: { maxWidth: { sm: '600px !important' } } } }}
    >
      <DialogTitle>Wartung eintragen</DialogTitle>
      <DialogContent>
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={de}>
          <Box sx={{ pt: 1 }}>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            <Grid container spacing={2}>
              <Grid size={12}>
                <TextField
                  select
                  fullWidth
                  label="Art"
                  value={form.type}
                  onChange={handleTypeChange}
                >
                  {MAINTENANCE_TYPES.map(option => (
                    <MenuItem key={option.key} value={option.key}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              {form.type === 'sonstiges' && (
                <Grid size={12}>
                  <TextField
                    fullWidth
                    required
                    label="Beschreibung"
                    placeholder="z. B. Reifenwechsel, Bremsen"
                    value={form.description}
                    onChange={(e) => updateForm({ description: e.target.value })}
                    error={descriptionMissing && form.description !== ''}
                  />
                </Grid>
              )}

              <Grid size={{ xs: 12, sm: 6 }}>
                <DatePicker
                  label="Durchgeführt am"
                  value={form.date}
                  onChange={(date) => updateForm({ date })}
                  format="dd.MM.yyyy"
                  disableFuture
                  slotProps={{ textField: { fullWidth: true, required: true } }}
                />
              </Grid>

              {type?.tracksDue && (
                <Grid size={{ xs: 12, sm: 6 }}>
                  <DatePicker
                    label="Nächste Fälligkeit"
                    value={form.nextDueDate}
                    onChange={(date) => {
                      setDueTouched(true);
                      setForm(prev => ({ ...prev, nextDueDate: date }));
                    }}
                    format="dd.MM.yyyy"
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        helperText: type.defaultIntervalMonths && !dueTouched
                          ? `Vorschlag: ${type.defaultIntervalMonths} Monate`
                          : 'Optional'
                      },
                      field: { clearable: true }
                    }}
                  />
                </Grid>
              )}

              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Kilometerstand"
                  inputMode="numeric"
                  value={form.odometer}
                  onChange={(e) => updateForm({ odometer: e.target.value.replace(/[^\d]/g, '') })}
                  slotProps={{ input: { endAdornment: <InputAdornment position="end">km</InputAdornment> } }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Kosten"
                  inputMode="decimal"
                  value={form.cost}
                  onChange={(e) => updateForm({ cost: e.target.value.replace(/[^\d.,]/g, '') })}
                  slotProps={{ input: { endAdornment: <InputAdornment position="end">€</InputAdornment> } }}
                />
              </Grid>
              <Grid size={12}>
                <TextField
                  fullWidth
                  label="Notizen"
                  multiline
                  minRows={2}
                  value={form.notes}
                  onChange={(e) => updateForm({ notes: e.target.value })}
                />
              </Grid>
            </Grid>

            {type?.tracksDue && currentDue && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                Bisher hinterlegt: {type.short} fällig am {currentDue.toLocaleDateString('de-DE')}.
                Eine spätere Fälligkeit ersetzt diesen Termin.
              </Typography>
            )}
          </Box>
        </LocalizationProvider>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving} color="inherit">
          Abbrechen
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          startIcon={<SaveIcon />}
          disabled={saving || descriptionMissing || dateInvalid}
        >
          {saving ? 'Wird gespeichert …' : 'Speichern'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MaintenanceRecordDialog;
